import { AuthenticationScheme } from '@see-power-pft-common-js/auth/enums';
import { AuthStoreAccess } from 'common-react/auth/auth-store';
import { useCipm3AuthScheme } from 'common-react/auth/schemes/cipm3/use-cipm3';
import { useCipm4AuthScheme } from 'common-react/auth/schemes/cipm4/use-cipm4';
import { useDepSsoAuthScheme } from 'common-react/auth/schemes/depsso/use-depsso';
import { useDevAuthScheme } from 'common-react/auth/schemes/dev/use-dev';
import {
  createContext,
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
} from 'react';

export interface AuthContextProps {
  authStoreAccess: AuthStoreAccess;
  logins: Partial<Record<AuthenticationScheme, () => Promise<void>>>;
  logout: () => Promise<void>;
  signalAuthError: (() => Promise<void>) | undefined;
}

export const AuthContext = createContext<AuthContextProps | undefined>(
  undefined,
);
AuthContext.displayName = 'AuthContext';

export interface IAuthScheme {
  isEnabled: boolean;
  login: () => Promise<void>;
  logout: () => Promise<void>;
  refresh: () => Promise<void>;
}

export type AuthActionHooks = {
  onStartLogin: () => void;
  onEndLogin: () => void;
  onStartLogout: () => void;
  onEndLogout: () => void;
};

export const AuthProvider: React.FC<
  PropsWithChildren<{
    authStoreAccess: AuthStoreAccess;
    actionHooks: AuthActionHooks;
  }>
> = (propsWithChildren) => {
  const props = useMemo(
    () => ({
      authStoreAccess: propsWithChildren.authStoreAccess,
      actionHooks: propsWithChildren.actionHooks,
    }),
    [propsWithChildren.authStoreAccess, propsWithChildren.actionHooks],
  );

  const devAuthScheme = useDevAuthScheme(props);
  const cipm4AuthScheme = useCipm4AuthScheme(props);
  const cipm3AuthScheme = useCipm3AuthScheme(props);
  const depSsoAuthScheme = useDepSsoAuthScheme(props);

  const authSchemes: Record<AuthenticationScheme, IAuthScheme> = useMemo(
    () => ({
      [AuthenticationScheme.DEV]: devAuthScheme,
      [AuthenticationScheme.CIPM3]: cipm3AuthScheme,
      [AuthenticationScheme.CIPM4]: cipm4AuthScheme,
      [AuthenticationScheme.DEPSSO]: depSsoAuthScheme,
    }),
    [devAuthScheme, cipm3AuthScheme, cipm4AuthScheme, depSsoAuthScheme],
  );

  useEffect(() => {
    // reset local state if invalid
    const authenticationScheme =
      props.authStoreAccess.getAuthenticationScheme();

    if (!authenticationScheme) {
      return;
    }

    if (
      authenticationScheme === AuthenticationScheme.DEV ||
      authenticationScheme === AuthenticationScheme.CIPM3 ||
      authenticationScheme === AuthenticationScheme.CIPM4 ||
      authenticationScheme === AuthenticationScheme.DEPSSO
    ) {
      return;
    }

    props.authStoreAccess.clearAuthState();

    authenticationScheme satisfies never;
  }, []);

  const logins = useMemo(
    () =>
      (
        Object.entries(authSchemes) as [AuthenticationScheme, IAuthScheme][]
      ).reduce(
        (acc, [key, scheme]) => {
          if (!scheme.isEnabled) {
            return acc;
          }

          acc[key] = scheme.login;

          return acc;
        },
        {} as Partial<Record<AuthenticationScheme, IAuthScheme['login']>>,
      ),
    [authSchemes],
  );

  const logout = useCallback(async () => {
    if (!props.authStoreAccess.getAuthenticationScheme()) {
      return;
    }
    if (!props.authStoreAccess.getJwtToken()) {
      return;
    }

    props.actionHooks.onStartLogout();

    await fetch(
      `${import.meta.env.VITE_PLUTO_API_ENDPOINT}/authentication/logout`,
      {
        headers: {
          authorization: `Bearer ${props.authStoreAccess.getJwtToken()}`,
        },
      },
    );

    props.authStoreAccess.setJwtToken(undefined);

    for (const scheme of Object.values(authSchemes)) {
      await scheme.logout();
    }
  }, [props, authSchemes]);

  const signalAuthError = useCallback(async () => {
    for (const scheme of Object.values(authSchemes)) {
      await scheme.refresh();
    }
  }, [authSchemes]);

  // value should never cause a re-render
  const value: AuthContextProps = useMemo(
    () => ({
      authStoreAccess: props.authStoreAccess,
      logins,
      logout,
      signalAuthError,
    }),
    [props.authStoreAccess, logins, logout, signalAuthError],
  );

  return (
    <AuthContext.Provider value={value} children={propsWithChildren.children} />
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error(`useAuth must be used within an AuthProvider`);
  }

  return context;
};
